/**
 * THE SESSION STORE — the one piece of the unified resolution surface that has no
 * backing endpoint in this app, held for the browser session and NOWHERE pretending
 * otherwise.
 *
 * **Guided ready-marks and the item ledger.** The studio stores
 * `runs.pending_issues[issueId] = {items}` so a fully-green issue stays on the
 * worklist as "ready" until the reviewer's explicit Continue, and so the issue's
 * item set survives a reload. There is no such column here.
 *
 * ── WHAT USED TO BE HERE AND IS NOT ANY MORE ─────────────────────────────────
 *
 * THE OVERRIDE NOTES. Every disposition's comment lived in this module because the
 * app had no override store; the surfaces that wrote one rendered `NotBackedNote`
 * beside it, and a reload lost the lot. The append-only `override_dispositions`
 * store landed with the review gestures, so the notes moved to it: written with
 * `POST /{id}/overrides`, RETIRED (never deleted) with
 * `POST /{id}/overrides/retire`, read back address-keyed off the read model, and
 * carrying the author and timestamp the SERVER stamped. Membership stayed derived —
 * `proceed_unknown` is a flag on `GET /{id}/statements` — so both classifier twins
 * see one answer instead of the browser holding a second, private one.
 *
 * The marks below did not move with them, and that is not an oversight: a "ready"
 * issue is a fact about where one reviewer is in their own pass, not a fact about
 * the declaration. Persisting it would put someone's place in the queue into the
 * record.
 *
 * WHY A MODULE STORE AND NOT REACT STATE. The four step surfaces are siblings that
 * mount and unmount as the reviewer moves between them, so state owned by any one
 * of them would evaporate on the first navigation — and mission control has to see
 * the same marks the steps wrote. Keyed by declaration id, so two declarations open
 * in two tabs never share one.
 *
 * It is deliberately NOT persisted to localStorage: a mark that survived a reload
 * would look like part of the declaration, which it is not.
 */
import { useCallback, useSyncExternalStore } from "react"

export interface FlowSession {
  /** `issueId(step, unitId)` → the item keys this issue opened with. An issue with
   *  a mark stays on the worklist as "ready" until Continue. */
  pendingIssues: Record<string, string[]>
}

const EMPTY: FlowSession = { pendingIssues: {} }

const sessions = new Map<string, FlowSession>()
const listeners = new Set<() => void>()

const emit = () => listeners.forEach((l) => l())

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

export function readFlowSession(declId: string): FlowSession {
  return sessions.get(declId) ?? EMPTY
}

function write(declId: string, next: FlowSession): void {
  sessions.set(declId, next)
  emit()
}

/** Mark an issue as edited-inside: it stays on the worklist as "ready" until it
 *  is explicitly resolved. `items` unions into the mark's ledger. */
export function markPendingIssue(
  declId: string,
  issueId: string,
  items: string[] = [],
): void {
  const cur = readFlowSession(declId)
  const merged = [...new Set([...(cur.pendingIssues[issueId] ?? []), ...items])]
  write(declId, {
    ...cur,
    pendingIssues: { ...cur.pendingIssues, [issueId]: merged },
  })
}

/** Clear one issue's mark — the reviewer's explicit resolution. */
export function clearPendingIssue(declId: string, issueId: string): void {
  const cur = readFlowSession(declId)
  if (!(issueId in cur.pendingIssues)) return
  const pendingIssues = { ...cur.pendingIssues }
  delete pendingIssues[issueId]
  write(declId, { ...cur, pendingIssues })
}

/** Drop everything a declaration accumulated (used by the tests). */
export function resetFlowSession(declId?: string): void {
  if (declId) sessions.delete(declId)
  else sessions.clear()
  emit()
}

/** The live session for one declaration. */
export function useFlowSession(declId: string): FlowSession {
  const get = useCallback(() => readFlowSession(declId), [declId])
  return useSyncExternalStore(subscribe, get, get)
}
